import React, { useState } from "react";
import { Activity, AlertTriangle, CheckCircle2, Clock, Play, RefreshCw, XCircle, Link2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "../../lib/api";
import { useApi } from "../../hooks/useApi";
import { Button } from "../../components/ui/button";

function Header({ title, sub }) {
  return <div className="mb-6"><p className="eyebrow text-gold">Officer Console</p><h1 className="font-serif-display text-3xl font-bold text-crimson">{title}</h1><p className="text-muted-foreground">{sub}</p></div>;
}

const STATE = {
  healthy: { label: "Healthy", cls: "bg-green-100 text-green-800", Icon: CheckCircle2 },
  overdue: { label: "Overdue", cls: "bg-red-100 text-red-800", Icon: AlertTriangle },
  failed: { label: "Failed", cls: "bg-red-100 text-red-800", Icon: XCircle },
  stuck: { label: "Stuck", cls: "bg-amber-100 text-amber-900", Icon: Clock },
  never_ran: { label: "Never ran", cls: "bg-stone-200 text-stone-700", Icon: Clock },
  disabled: { label: "Disabled", cls: "bg-stone-200 text-stone-600", Icon: XCircle },
};
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");
const ago = (iso) => {
  if (!iso) return "never";
  const m = Math.round((Date.now() - new Date(iso)) / 60000);
  if (m < 60) return `${m} min ago`;
  if (m < 2880) return `${Math.round(m / 60)} h ago`;
  return `${Math.round(m / 1440)} d ago`;
};
const runCls = (status) => {
  if (["failed", "error"].includes(status)) return "bg-red-100 text-red-800";
  if (["done", "ok"].includes(status)) return "bg-green-100 text-green-800";
  return "bg-amber-100 text-amber-900";
};

function Pill({ state }) { const s = STATE[state] || STATE.never_ran; return <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${s.cls}`} data-testid={`cron-state-${state}`}><s.Icon size={12} /> {s.label}</span>; }

function Runs({ job }) {
  const { data } = useApi(`/admin/cron/runs?job=${job}&limit=10`);
  if (!data) return <p className="p-3 text-xs text-muted-foreground">Loading…</p>;
  if (!data.runs.length) return <p className="p-3 text-xs text-muted-foreground">No runs recorded yet.</p>;
  return (
    <ul className="divide-y divide-gold/15 text-xs" data-testid={`cron-runs-${job}`}>
      {data.runs.map((r) => (
        <li key={r.run_id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2">
          <span className="font-mono-accent">{fmt(r.started_at)}</span>
          <span className={`rounded px-1.5 py-0.5 ${runCls(r.status)}`}>{r.status}</span>
          <span className="text-muted-foreground">{Object.entries(r.detail || {}).filter(([k]) => !["run_id", "by"].includes(k)).map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : v}`).join(" · ")}</span>
        </li>
      ))}
    </ul>
  );
}

function JobRow({ j, onRun }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`rounded-xl border ${["overdue", "failed", "stuck"].includes(j.state) ? "border-red-300 bg-red-50/60" : "border-gold/25 bg-parchment-card"}`} data-testid={`cron-job-${j.name}`}>
      <div className="flex flex-col gap-3 p-4 lg:flex-row lg:items-start">
        <div className="min-w-0 flex-1">
          <p className="flex flex-wrap items-center gap-2 font-semibold">{j.kind === "embedded" && <Link2 size={14} className="text-muted-foreground" />}{j.name} <Pill state={j.state} /></p>
          <p className="text-xs text-muted-foreground">{j.description}</p>
          <p className="mt-1 font-mono-accent text-[11px] text-muted-foreground">{j.kind === "embedded" ? `inside ${j.parent}` : `${j.cron} · ${j.timezone}`}</p>
        </div>
        <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-xs lg:grid-cols-[auto_auto] lg:text-right">
          <span className="text-muted-foreground">Last run</span><span data-testid={`cron-last-${j.name}`}>{fmt(j.last?.started_at)} <span className="text-muted-foreground">({ago(j.last?.started_at)})</span></span>
          {j.expected_next && <><span className="text-muted-foreground">Next due</span><span>{fmt(j.expected_next)}</span></>}
          <span className="text-muted-foreground">Failures (7d)</span><span className={j.failures_7d ? "font-semibold text-crimson" : ""}>{j.failures_7d}</span>
        </div>
        <div className="flex gap-2 lg:shrink-0">
          <Button size="sm" variant="outline" className="h-9" onClick={() => setOpen(!open)} data-testid={`cron-history-${j.name}`}>{open ? "Hide" : "History"}</Button>
          {j.kind === "schedule" && <Button size="sm" className="h-9 gap-1 bg-crimson" onClick={() => onRun(j.name)} data-testid={`cron-run-${j.name}`}><Play size={13} /> Run now</Button>}
        </div>
      </div>
      {open && <div className="border-t border-gold/15"><Runs job={j.name} /></div>}
    </div>
  );
}

export default function CronHealth() {
  const { data, reload } = useApi("/admin/cron/health");
  const run = async (name) => {
    if (!window.confirm(`Run "${name}" now? It will do real work (e.g. send reminders that are due).`)) return;
    try { const { data: r } = await api.post(`/admin/cron/run/${name}`); toast.success(`${name} triggered (${r.response?.status || "accepted"})`); setTimeout(reload, 1500); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  if (!data) return <p className="text-muted-foreground">Loading…</p>;
  const ok = data.problems.length === 0 && data.over_limit.length === 0 && data.secret_configured;
  return (
    <div data-testid="cron-health">
      <Header title="Scheduled Jobs" sub="Health of every platform cron: last run, next due, failures. Flags anything overdue, failed or stuck." />
      <div className={`mb-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border-2 p-4 ${ok ? "border-palm/40 bg-palm/10" : "border-red-400 bg-red-50"}`} data-testid="cron-summary">
        <div className="flex items-center gap-3">
          <Activity size={22} className={ok ? "text-palm" : "text-crimson"} />
          <div>
            <p className="font-serif-display text-lg font-bold">{ok ? "All scheduled jobs are healthy" : `${data.problems.length + data.over_limit.length + (data.secret_configured ? 0 : 1)} issue(s) need attention`}</p>
            <p className="text-xs text-muted-foreground">{data.schedules_enabled} of {data.schedule_limit} schedule slots used
              {data.over_limit.length > 0 && <span className="font-semibold text-crimson"> · over limit: {data.over_limit.join(", ")} will not run</span>}
              {!data.secret_configured && <span className="font-semibold text-crimson"> · WEBHOOK_CRON_SECRET missing</span>}
              {data.problems.length > 0 && <span className="font-semibold text-crimson"> · {data.problems.join(", ")}</span>}</p>
          </div>
        </div>
        <Button variant="outline" className="h-9 gap-1" onClick={reload} data-testid="cron-refresh"><RefreshCw size={14} /> Refresh</Button>
      </div>
      <div className="space-y-3">{data.jobs.map((j) => <JobRow key={j.name} j={j} onRun={run} />)}</div>
    </div>
  );
}
